import { Component, Input, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DonationCardService } from '../../../services/donation-card.service';
import { DonationFundraiserViewModel, DropdownOption } from '../../../models/DonationCardModels';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { GenericButtonComponent } from '../../../../global/component/generic/generic-button/generic-button.component';

@Component({
  selector: 'app-donation-fundraisers-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, CardModule, TableModule, AutoCompleteModule, DialogModule, InputTextModule, ButtonModule, GenericButtonComponent],
  providers: [MessageService],
  templateUrl: './donation-fundraisers-tab.component.html',
  styleUrl: './donation-fundraisers-tab.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA] 
})
export class DonationFundraisersTabComponent implements OnInit {
  @Input() donationId!: number;
  @Input() campaignId!: number;
  @Input() isReadOnlyMode: boolean = false;
  fundraisers: DonationFundraiserViewModel[] = [];
  showAddFundraiserDialog: boolean = false;
  showDeleteDialog: boolean = false; // Track delete dialog visibility
  fundraiserToDelete: number | null = null; // Track fundraiser to delete
  fundraiserSuggestions: DropdownOption[] = [];
  selectedFundraiser: DropdownOption | null = null;

  constructor(
    private service: DonationCardService,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.loadFundraisers();
  }

  loadFundraisers(): void {
    this.service.getFundraisers(this.donationId).subscribe({
      next: (data) => {
        this.fundraisers = data;
      },
      error: (err) => {
        console.error('Error fetching fundraisers:', err);
      }
    });
  }

  searchFundraisers(event: any): void {
    const query = event.query;
    const currentFundraiserId = this.selectedFundraiser?.value || 0;
    this.service.searchFundraisers(query, this.campaignId, currentFundraiserId).subscribe({
      next: (data) => {
        this.fundraiserSuggestions = data.map((item: any) => ({
          text: item.text,
          value: Number(item.value)
        }));
      },
      error: (err) => {
        console.error('Error searching fundraisers:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to search fundraisers. Please try again.'
        });
      }
    });
  }

  addFundraiser(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.selectedFundraiser) {
      this.service.addFundraiserToDonation(this.donationId, this.selectedFundraiser.value).subscribe({
        next: () => {
          this.loadFundraisers();
          this.showAddFundraiserDialog = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Fundraiser added successfully.'
          });
        },
        error: (err) => {
          console.error('Error adding fundraiser:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add fundraiser. Please try again.'
          });
        }
      });
    }
  }

  confirmDeleteFundraiser(fundraiserId: number): void {
    this.fundraiserToDelete = fundraiserId; // Set fundraiser to delete
    this.showDeleteDialog = true; // Show delete dialog
  }

  deleteFundraiser(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.fundraiserToDelete !== null) {
      this.service.deleteFundraiserFromDonation(this.donationId, this.fundraiserToDelete).subscribe({
        next: () => {
          this.loadFundraisers();
          this.closeDeleteDialog(); // Close delete dialog
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Fundraiser deleted successfully.'
          });
        },
        error: (err) => {
          console.error('Error deleting fundraiser:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete fundraiser. Please try again.'
          });
        }
      });
    }
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog = false; // Close delete dialog
  }

  navigateToFundraiser(fundraiserId: number): void {
    this.router.navigate(['/fundraiser', fundraiserId]);
  }

  openAddFundraiserDialog(): void {
    this.selectedFundraiser = null; // Clear selected fundraiser
    this.fundraiserSuggestions = []; // Clear suggestions
    this.showAddFundraiserDialog = true; // Open modal
  }
}
